import { useEffect, useRef, useState } from 'react';
import { Box, Grid2 as Grid, IconButton, Tab, Tabs } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Panel } from 'reactflow';
import { color, EL_COLORS } from '@/colors';
import { WorkflowEditorMode } from '@/enums/workflow-editor-mode.enum';
import { FlexCenVer } from '@/UI/styles';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_WORKFLOW_APP_BUILDER_MODE } from '@/consts/featureFlags';
import { CreditsInfo } from '../FlowComponents/CreditsInfo/CreditsInfo';
import { WorkflowRightToolbar } from '../FlowComponents/WorkflowRightToolbar/WorkflowRightToolbar';
import { useWorkflowEditorMode } from '../FlowComponents/WorkflowRightToolbar/workflowEditorMode.store';
import { PANEL_WIDTH } from '../consts/ui';
import { WorkflowPropertiesPanel, hasDisplayableWorkflowProperties } from './WorkflowPropertiesPanel';
import { AppBuilderPanel } from './AppBuilderPanel';
import type { Node } from '@/types/node';

const AI_PANEL_WIDTH = 320;

type RightPanelTab = 'properties' | 'ai';

interface WorkflowRightPanelProps {
  selectedNodes: Node[];
  renderContent?: React.ReactNode;
  forceOpen?: boolean;
  hasChatMessages?: boolean;
  onClearChat?: () => void;
  onOpenAI?: () => void;
}

function WorkflowRightPanelBody({
  workflowEditorMode,
  selectedNodes,
}: {
  workflowEditorMode: WorkflowEditorMode;
  selectedNodes: Node[];
}) {
  switch (workflowEditorMode) {
    case WorkflowEditorMode.AppBuilder:
      return <AppBuilderPanel />;
    case WorkflowEditorMode.EditWorkflow:
      return <WorkflowPropertiesPanel selectedNodes={selectedNodes} />;
  }
}

export function WorkflowRightPanel({
  selectedNodes,
  renderContent,
  forceOpen,
  hasChatMessages,
  onClearChat,
  onOpenAI,
}: WorkflowRightPanelProps) {
  const { workflowEditorMode } = useWorkflowEditorMode();
  const { value: isWorkflowAppBuilderEnabled } = useFeatureFlag(FF_WORKFLOW_APP_BUILDER_MODE);

  const hasProperties =
    workflowEditorMode === WorkflowEditorMode.AppBuilder || hasDisplayableWorkflowProperties(selectedNodes);
  const isAI = !!renderContent;
  const hasUsedAI = useRef(false);
  if (isAI) hasUsedAI.current = true;
  const showAITab = isAI || hasUsedAI.current;
  const hasSelection = selectedNodes.length > 0;
  const isPanelOpen = forceOpen || hasProperties || (hasSelection && hasUsedAI.current);

  const [activeTab, setActiveTab] = useState<RightPanelTab>(isAI ? 'ai' : 'properties');

  // When AI opens, switch to AI tab
  useEffect(() => {
    if (isAI) setActiveTab('ai');
  }, [isAI]);

  // When properties appear (node selected), switch to properties tab
  // When properties disappear and AI is active, switch back to AI tab
  const prevHasProperties = useRef(hasProperties);
  useEffect(() => {
    if (hasProperties && !prevHasProperties.current) {
      setActiveTab('properties');
    } else if (!hasProperties && prevHasProperties.current && showAITab) {
      setActiveTab('ai');
    }
    prevHasProperties.current = hasProperties;
  }, [hasProperties, isAI]);

  const showTabs = showAITab && (isAI || hasProperties);
  const showAI = isAI && activeTab === 'ai';
  const showProperties = activeTab === 'properties' && hasProperties;

  const handleTabChange = (_: unknown, value: RightPanelTab) => {
    setActiveTab(value);
    if (value === 'ai' && !isAI && onOpenAI) {
      onOpenAI();
    }
  };
  const panelWidth = showAI ? AI_PANEL_WIDTH : PANEL_WIDTH;

  return (
    <Panel
      position="top-right"
      style={{
        height: '100%',
        width: `${panelWidth}px`,
        top: '0px',
        right: '0px',
        margin: 0,
      }}
      className={isPanelOpen ? 'slide-right-enter' : 'slide-right-exit'}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: color.Black92,
          borderLeft: `1px solid ${EL_COLORS.BoxBorder}`,
        }}
      >
        {isPanelOpen && (
          <>
            {/* Tabs — always shown when AI is active */}
            {showTabs && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    flex: 1,
                    minHeight: 36,
                    '& .MuiTab-root': {
                      minHeight: 36,
                      py: 0,
                      px: 1.5,
                      fontSize: '12px',
                      fontWeight: 600,
                      color: color.White64_T,
                      textTransform: 'none',
                      '&.Mui-selected': { color: color.White100 },
                    },
                    '& .MuiTabs-indicator': { backgroundColor: color.White100, height: 1.5 },
                  }}
                >
                  <Tab
                    value="ai"
                    label="AI Weaver"
                    icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
                    iconPosition="start"
                    sx={{ '& .MuiTab-iconWrapper': { mr: 0.5 } }}
                  />
                  {hasProperties && <Tab value="properties" label="Properties" />}
                </Tabs>
                {activeTab === 'ai' && hasChatMessages && onClearChat && (
                  <IconButton onClick={onClearChat} size="small" sx={{ color: color.White40_T, p: 0.25, mr: 1 }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            )}

            {/* AI content */}
            {showAI && renderContent}

            {/* Properties content */}
            {showProperties && !showAI && (
              <Grid container direction="column" sx={{ height: showTabs ? 'calc(100% - 36px)' : '100%' }}>
                <Grid size="auto">
                  <FlexCenVer
                    data-testid="properties-title"
                    sx={{
                      borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
                      pl: 1.5,
                      pr: 2,
                      height: '84px',
                    }}
                  >
                    {isWorkflowAppBuilderEnabled ? (
                      <WorkflowRightToolbar isPaperBg={false} />
                    ) : (
                      <CreditsInfo isPaperBg={false} />
                    )}
                  </FlexCenVer>
                </Grid>

                <WorkflowRightPanelBody workflowEditorMode={workflowEditorMode} selectedNodes={selectedNodes} />
              </Grid>
            )}
          </>
        )}
      </Box>
    </Panel>
  );
}
